export type WarehouseAdminRestockStatus = "created"|"ordered"|"checked"|"received";

export interface IWarehouseAdminRestockSupplierData {
    id: number;
    supplier_id: number|null;
    supplier_name: string|null;
    legacy_supplier_id?: number|null;
    legacy_supplier_name?: string|null;
    price: number;
    removed?: boolean;
}

export interface IWarehouseAdminRestockProductData {
    id: number;
    name: string;
    description?: string|null;
    target_price?: number|null;
    targetPrice?: number|null;
}

export interface IWarehouseAdminRestockWarehouseStockData {
    last_stock_count?: number;
    received_after_stock_count?: number;
    delivered_after_stock_count?: number;
    calculated_stock?: number;
    open_restock?: number;
}

export interface IWarehouseAdminRestockCoreData {
    id: number;
    product: number;
    amount: number;
    product_supplier?: number|null;
    productSupplier?: number|null;
    created?: string|null;
    created_by?: string|null;
    createdBy?: string|null;
    ordered?: string|null;
    received?: string|null;
    received_by?: string|null;
    receivedBy?: string|null;
    received_checked_at?: string|null;
    receivedCheckedAt?: string|null;
    received_checked_by?: string|null;
    receivedCheckedBy?: string|null;
    received_amount?: number|null;
    receivedAmount?: number|null;
    received_damaged?: number|null;
    receivedDamaged?: number|null;
    received_price?: number|null;
    receivedPrice?: number|null;
    received_note?: string|null;
    receivedNote?: string|null;
    received_create_remainder?: boolean;
    receivedCreateRemainder?: boolean;
}

export interface IWarehouseAdminRestockData {
    restock?: IWarehouseAdminRestockCoreData;

    id?: number;
    product_id?: number;
    amount?: number;
    product_supplier?: number|null;
    productSupplier?: number|null;
    created?: string|null;
    created_by?: string|null;
    createdBy?: string|null;
    ordered?: string|null;
    received?: string|null;
    received_by?: string|null;
    receivedBy?: string|null;
    received_checked_at?: string|null;
    receivedCheckedAt?: string|null;
    received_checked_by?: string|null;
    receivedCheckedBy?: string|null;
    received_amount?: number|null;
    receivedAmount?: number|null;
    received_damaged?: number|null;
    receivedDamaged?: number|null;
    received_price?: number|null;
    receivedPrice?: number|null;
    received_note?: string|null;
    receivedNote?: string|null;
    received_create_remainder?: boolean;
    receivedCreateRemainder?: boolean;

    status: WarehouseAdminRestockStatus|string;
    product: IWarehouseAdminRestockProductData;
    suppliers?: IWarehouseAdminRestockSupplierData[];
    selected_supplier?: IWarehouseAdminRestockSupplierData|null;
    selectedSupplier?: IWarehouseAdminRestockSupplierData|null;
    warehouse_stock?: IWarehouseAdminRestockWarehouseStockData|null;
    warehouseStock?: IWarehouseAdminRestockWarehouseStockData|null;
    history_90_days?: number;
    history90Days?: number;
    history_365_days?: number;
    history365Days?: number;
    last_restock_price?: number|null;
    lastRestockPrice?: number|null;
    lowest_restock_price?: number|null;
    lowestRestockPrice?: number|null;
}

function nullableNumber(value: number|string|null|undefined): number|null {
    if (value === null || value === undefined || value === "") return null;
    return Number(value);
}

export default class WarehouseAdminRestockData {
    public id: number;
    public product_id: number;
    public amount: number;
    public product_supplier: number|null;
    public created: string|null;
    public created_by: string|null;
    public ordered: string|null;
    public received: string|null;
    public received_by: string|null;
    public received_checked_at: string|null;
    public received_checked_by: string|null;
    public received_amount: number|null;
    public received_damaged: number|null;
    public received_price: number|null;
    public received_note: string|null;
    public received_create_remainder: boolean;
    public status: WarehouseAdminRestockStatus|string;
    public product: {
        id: number;
        name: string;
        description: string|null;
        target_price: number|null;
    };
    public suppliers: IWarehouseAdminRestockSupplierData[];
    public selected_supplier: IWarehouseAdminRestockSupplierData|null;
    public warehouse_stock: {
        last_stock_count: number;
        received_after_stock_count: number;
        delivered_after_stock_count: number;
        calculated_stock: number;
        open_restock: number;
    }|null;
    public history_90_days: number;
    public history_365_days: number;
    public last_restock_price: number|null;
    public lowest_restock_price: number|null;

    constructor(data: IWarehouseAdminRestockData) {
        const restock = data.restock;

        this.id = Number(restock?.id ?? data.id);
        this.product_id = Number(restock?.product ?? data.product_id ?? data.product.id);
        this.amount = Number(restock?.amount ?? data.amount ?? 0);
        this.product_supplier = nullableNumber(restock?.product_supplier ?? restock?.productSupplier ?? data.product_supplier ?? data.productSupplier);
        this.created = restock?.created ?? data.created ?? null;
        this.created_by = restock?.created_by ?? restock?.createdBy ?? data.created_by ?? data.createdBy ?? null;
        this.ordered = restock?.ordered ?? data.ordered ?? null;
        this.received = restock?.received ?? data.received ?? null;
        this.received_by = restock?.received_by ?? restock?.receivedBy ?? data.received_by ?? data.receivedBy ?? null;
        this.received_checked_at = restock?.received_checked_at ?? restock?.receivedCheckedAt ?? data.received_checked_at ?? data.receivedCheckedAt ?? null;
        this.received_checked_by = restock?.received_checked_by ?? restock?.receivedCheckedBy ?? data.received_checked_by ?? data.receivedCheckedBy ?? null;
        this.received_amount = nullableNumber(restock?.received_amount ?? restock?.receivedAmount ?? data.received_amount ?? data.receivedAmount);
        this.received_damaged = nullableNumber(restock?.received_damaged ?? restock?.receivedDamaged ?? data.received_damaged ?? data.receivedDamaged);
        this.received_price = nullableNumber(restock?.received_price ?? restock?.receivedPrice ?? data.received_price ?? data.receivedPrice);
        this.received_note = restock?.received_note ?? restock?.receivedNote ?? data.received_note ?? data.receivedNote ?? null;
        this.received_create_remainder = Boolean(
            restock?.received_create_remainder
            ?? restock?.receivedCreateRemainder
            ?? data.received_create_remainder
            ?? data.receivedCreateRemainder
            ?? false
        );

        this.status = data.status;

        this.product = {
            id: Number(data.product.id),
            name: data.product.name,
            description: data.product.description ?? null,
            target_price: nullableNumber(data.product.target_price ?? data.product.targetPrice)
        };

        this.suppliers = (data.suppliers ?? []).filter(supplier => !supplier.removed).map(supplier => ({
            ...supplier,
            id: Number(supplier.id),
            supplier_id: nullableNumber(supplier.supplier_id),
            price: Number(supplier.price ?? 0)
        }));

        this.selected_supplier = data.selected_supplier ?? data.selectedSupplier ?? null;

        const warehouseStock = data.warehouse_stock ?? data.warehouseStock ?? null;

        this.warehouse_stock = warehouseStock ? {
            last_stock_count: Number(warehouseStock.last_stock_count ?? 0),
            received_after_stock_count: Number(warehouseStock.received_after_stock_count ?? 0),
            delivered_after_stock_count: Number(warehouseStock.delivered_after_stock_count ?? 0),
            calculated_stock: Number(warehouseStock.calculated_stock ?? 0),
            open_restock: Number(warehouseStock.open_restock ?? 0)
        } : null;

        this.history_90_days = Number(data.history_90_days ?? data.history90Days ?? 0);
        this.history_365_days = Number(data.history_365_days ?? data.history365Days ?? 0);
        this.last_restock_price = nullableNumber(data.last_restock_price ?? data.lastRestockPrice);
        this.lowest_restock_price = nullableNumber(data.lowest_restock_price ?? data.lowestRestockPrice);
    }
}